import { PlusIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/core/Button';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { Grid } from '@/components/core/Grid';
import { Panel, PanelHeader } from '@/components/core/Panel';
import { PanelBody } from '@/components/core/Panel/PanelBody';
import ConfirmationModal from '@/dialogs/confirmation-modals/ConfirmationModal';
import useRealTimeMessaging from '@/hooks/useRealtimeMessaging';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { RealTimeMessagingEvents } from '@/types/RealTimeMessagingEvents';

import AddUserGroupPage from './AddUserGroupPage';
import { columns } from './columns';
import { isHopscotchTourActive, takeUserGroupToast } from './user-groups.location';
import { useDeleteUserGroup, useUserGroups } from './user-groups.queries';
import { sentenceCase, UserGroup } from './user-groups.types';

export default function UserGroupsPage() {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { data, isLoading, refetch } = useUserGroups();
    const [addOpen, setAddOpen] = useState(false);
    const [editingGroupId, setEditingGroupId] = useState<string | undefined>();
    const [pendingDelete, setPendingDelete] = useState<UserGroup | null>(null);
    const [actionState, setActionState] = useState({ saveDisabled: true, saving: false });
    const actionsRef = useRef<{ save: () => void; cancel: () => void } | null>(null);
    const onActionStateChange = useCallback((
        next: { saveDisabled: boolean; saving: boolean },
    ) => {
        setActionState((current) => (
            current.saveDisabled === next.saveDisabled && current.saving === next.saving
                ? current
                : next
        ));
    }, []);

    const { mutate: deleteGroup } = useDeleteUserGroup({
        onSuccess: () => {
            toast.success('USER_GROUP.DELETE_SUCCESS');
            setPendingDelete(null);
            refetch();
        },
    });

    const openCreate = () => {
        setEditingGroupId(undefined);
        setActionState({ saveDisabled: true, saving: false });
        setAddOpen(true);
    };

    const openEdit = (groupId: number | string) => {
        setEditingGroupId(String(groupId));
        setActionState({ saveDisabled: true, saving: false });
        setAddOpen(true);
    };

    const closeForm = () => {
        setAddOpen(false);
        setEditingGroupId(undefined);
    };

    useEffect(() => {
        const messageKey = takeUserGroupToast();
        if (messageKey) {
            toast.success(messageKey);
        }
    }, [toast]);

    const onGroupsChanged = useCallback(() => {
        if (isHopscotchTourActive()) {
            return;
        }
        refetch();
    }, [refetch]);

    useRealTimeMessaging({
        eventName: RealTimeMessagingEvents.USERGROUPS_CHANGED,
        eventHandler: onGroupsChanged,
    });

    return (
        <div className="tw-flex tw-flex-col">
            <PanelHeader
                className="tw-shrink-0 !tw-pb-4"
                icon={UserGroupIcon}
                title={sentenceCase(t('USER_GROUP.USER_GROUPS'))}
                actions={(
                    <Button
                        id="create-group-btn"
                        hasIcon
                        icon={PlusIcon}
                        className="tw-shrink-0"
                        onClick={openCreate}
                    >
                        {sentenceCase(t('USER_GROUP.ADD_GROUP'))}
                    </Button>
                )}
            />
            <Panel>
                <PanelBody>
                    <Grid
                        id="users-grid"
                        columns={columns({
                            t,
                            onEdit: openEdit,
                            onDelete: setPendingDelete,
                        })}
                        data={data ?? []}
                        isLoading={isLoading}
                        storedStateKey="UserGroups"
                        isWrapContent
                        className="tw-h-full"
                    />
                </PanelBody>
            </Panel>
            <Dialog open={addOpen} onOpenChange={(open) => {
                if (!open) {
                    actionsRef.current?.cancel();
                }
            }}>
                <DialogContent
                    variant="large"
                    className="modal-dialog !tw-bottom-16 !tw-max-h-[calc(100vh-8rem)] !tw-overflow-hidden lg:!tw-bottom-auto"
                    onPointerDownOutside={(event) => event.preventDefault()}
                    onInteractOutside={(event) => event.preventDefault()}
                >
                    <DialogHeader>
                        <DialogHeaderDefault>
                            {sentenceCase(editingGroupId
                                ? t('USER_GROUP.EDIT_GROUP')
                                : t('USER_GROUP.ADD_GROUP'))}
                        </DialogHeaderDefault>
                    </DialogHeader>
                    <DialogBody scrollable className="tw-max-h-[calc(100vh-16rem)]">
                        {addOpen ? (
                            <AddUserGroupPage
                                key={editingGroupId ?? 'create'}
                                embedded
                                groupId={editingGroupId}
                                actionsRef={actionsRef}
                                onActionStateChange={onActionStateChange}
                                onFinished={closeForm}
                            />
                        ) : null}
                    </DialogBody>
                    <DialogFooter className="tw-items-center">
                        <Button
                            id="groupSaveButton"
                            disabled={actionState.saveDisabled}
                            loading={actionState.saving}
                            onClick={() => actionsRef.current?.save()}
                        >
                            {sentenceCase(t('USER_GROUP.SAVE_GROUP'))}
                        </Button>
                        <Button
                            className="!tw-h-auto"
                            variant="secondary"
                            onClick={() => actionsRef.current?.cancel()}
                        >
                            {t('CANCEL')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <ConfirmationModal
                show={pendingDelete != null}
                title="CONFIRM_DELETE"
                subTitle="USER_GROUP.REMOVE_GROUPS"
                btnVariant="destructive"
                onSubmit={() => {
                    if (pendingDelete?.id == null) {
                        return;
                    }
                    deleteGroup(pendingDelete.id);
                }}
                onClose={() => setPendingDelete(null)}
            />
        </div>
    );
}
